import PasswordField from "../components/common/PasswordField";
import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthCardLayout from "../components/forms/AuthCardLayout";
import OtpStep from "../components/forms/OtpStep";
import AuthService from "../services/AuthService";
import { getErrorMessage } from "../services/apiUtils";

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = React.useState<"request" | "reset">("request");
  const [identifier, setIdentifier] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleRequestOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await AuthService.sendPasswordResetOtp(identifier);
      setStep("reset");
    } catch (err) {
      setError(getErrorMessage(err, "Could not send a reset code. Please check the email/phone and try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    setError(null);
    if (!newPassword) {
      setError("Please enter a new password.");
      return;
    }
    setIsSubmitting(true);
    try {
      await AuthService.resetPassword({ identifier, otp, newPassword });
      navigate("/login");
    } catch (err) {
      setError(getErrorMessage(err, "Invalid or expired code. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === "reset") {
    return (
      <AuthCardLayout title={t("auth.resetTitle")}>
        <OtpStep
          identifier={identifier}
          otp={otp}
          onOtpChange={setOtp}
          onVerify={handleReset}
          onResend={() => AuthService.sendPasswordResetOtp(identifier)}
          isVerifying={isSubmitting}
          error={error}
          submitLabel={t("auth.resetPassword")}
          extraFields={
            <PasswordField
              margin="normal"
              required
              fullWidth
              label={t("auth.newPassword")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          }
        />
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout title={t("auth.forgotTitle")}>
      <Box component="form" noValidate onSubmit={handleRequestOtp} sx={{ width: "100%" }}>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          margin="normal"
          required
          fullWidth
          label={t("auth.emailOrMobile")}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          autoFocus
        />
        <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={{ mt: 3, mb: 2 }}>
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t("auth.sendResetCode")}
        </Button>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Link component="button" type="button" variant="body2" onClick={() => navigate("/login")}>
            {t("auth.backToSignIn")}
          </Link>
        </Box>
      </Box>
    </AuthCardLayout>
  );
};

export default ForgotPasswordPage;
